import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import PageContainer from '../components/layout/PageContainer';
import { Requisition } from '../types';
import { mockRecruitmentData } from '../utils/mockRecruitmentData';

const ContentArea = styled.div`
  padding: 0 ${Spacing.xl}px ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding-left: ${Spacing.md}px;
    padding-right: ${Spacing.md}px;
  }
`;

const RequisitionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: ${Spacing.xl}px;
`;

const RequisitionCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
  transition: all 150ms ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${Shadows.md};
  }
`;

const CardHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
`;

const CardTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${Spacing.md}px;
`;

const ReqTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background-color: ${props => {
    switch (props.status) {
      case 'OPEN': return Colors.success;
      case 'ON_HOLD': return Colors.warning;
      case 'CLOSED': return Colors.borderDark;
      case 'FILLED': return Colors.accent;
      default: return Colors.borderLight;
    }
  }};
  color: ${Colors.textOnPrimary};
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.md}px;
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const CardBody = styled.div`
  padding: ${Spacing.lg}px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.lg}px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Pipeline = styled.div`
  margin-bottom: ${Spacing.lg}px;
`;

const PipelineLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.sm}px;
`;

const PipelineBar = styled.div`
  display: flex;
  height: 32px;
  border-radius: ${BorderRadius.sm}px;
  overflow: hidden;
  background-color: ${Colors.backgroundDark};
`;

const PipelineSegment = styled.div<{ stage: string; width: number }>`
  width: ${props => props.width}%;
  background-color: ${props => {
    switch (props.stage) {
      case 'applied': return Colors.info;
      case 'screening': return Colors.warning;
      case 'interview': return Colors.primary;
      case 'offer': return Colors.success;
      case 'hired': return Colors.accent;
      default: return Colors.borderLight;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CardFooter = styled.div`
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ManagerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.round}px;
  background-color: ${Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
`;

const ManagerName = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const SLAIndicator = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: 4px 12px;
  border-radius: ${BorderRadius.sm}px;
  background-color: ${props => {
    switch (props.status) {
      case 'ON_TRACK': return `${Colors.success}20`;
      case 'AT_RISK': return `${Colors.warning}20`;
      case 'BREACHED': return `${Colors.danger}20`;
      default: return Colors.backgroundDark;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'ON_TRACK': return Colors.success;
      case 'AT_RISK': return Colors.warning;
      case 'BREACHED': return Colors.danger;
      default: return Colors.textSecondary;
    }
  }};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
`;

const QuickFilters = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.xl}px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: ${Spacing.sm}px ${Spacing.lg}px;
  background-color: ${props => props.active ? Colors.primary : Colors.surface};
  color: ${props => props.active ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
    border-color: ${props => props.active ? Colors.primaryDark : Colors.borderDark};
  }
`;

const CreateButton = styled.button`
  position: fixed;
  bottom: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  width: 56px;
  height: 56px;
  border-radius: ${BorderRadius.round}px;
  background-color: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  box-shadow: ${Shadows.lg};
  font-size: ${FontSizes.xxl}px;
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  
  &:hover {
    background-color: ${Colors.primaryDark};
    transform: scale(1.1);
  }
`;

const RequisitionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [requisitions] = useState<Requisition[]>(mockRecruitmentData.requisitions);
  const [filter, setFilter] = useState<'ALL' | 'OPEN' | 'AT_RISK' | 'HIGH_PRIORITY'>('ALL');

  const filteredRequisitions = requisitions.filter(req => {
    switch (filter) {
      case 'OPEN':
        return req.status === 'OPEN';
      case 'AT_RISK':
        return req.slaStatus === 'AT_RISK' || req.slaStatus === 'BREACHED';
      case 'HIGH_PRIORITY':
        return req.priority === 'HIGH';
      default:
        return true;
    }
  });

  const handleRequisitionClick = (requisitionId: string) => {
    navigate(`/requisitions/${requisitionId}`);
  };

  const getTotalCandidates = (stageCount: Requisition['stageCount']) => {
    return Object.values(stageCount).reduce((sum, count) => sum + count, 0);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <PageContainer>
      <UnifiedPageHeader
        title="My Requisitions"
        subtitle="Track and manage all your open positions"
        metadata={{
          timestamp: new Date(),
          count: filteredRequisitions.length,
          status: 'Active'
        }}
        actions={[
          {
            label: 'Add Requisition',
            onClick: () => console.log('Add requisition clicked'),
            variant: 'primary',
            icon: '+'
          }
        ]}
      />
      
      <ContentArea>
        <QuickFilters>
        <FilterButton 
          active={filter === 'ALL'} 
          onClick={() => setFilter('ALL')}
        >
          All Requisitions ({requisitions.length})
        </FilterButton>
        <FilterButton 
          active={filter === 'OPEN'} 
          onClick={() => setFilter('OPEN')}
        >
          Open ({requisitions.filter(r => r.status === 'OPEN').length})
        </FilterButton>
        <FilterButton 
          active={filter === 'AT_RISK'} 
          onClick={() => setFilter('AT_RISK')}
        >
          At Risk ({requisitions.filter(r => r.slaStatus === 'AT_RISK' || r.slaStatus === 'BREACHED').length})
        </FilterButton>
        <FilterButton 
          active={filter === 'HIGH_PRIORITY'} 
          onClick={() => setFilter('HIGH_PRIORITY')}
        >
          High Priority ({requisitions.filter(r => r.priority === 'HIGH').length})
        </FilterButton>
      </QuickFilters>

      <RequisitionsGrid>
        {filteredRequisitions.map(req => {
          const totalCandidates = getTotalCandidates(req.stageCount);
          
          return (
            <RequisitionCard 
              key={req.id}
              onClick={() => handleRequisitionClick(req.id)}
            >
              <CardHeader>
                <CardTitle>
                  <ReqTitle>{req.title}</ReqTitle>
                  <StatusBadge status={req.status}>{req.status}</StatusBadge>
                </CardTitle>
                <MetaInfo>
                  <MetaItem>📍 {req.location}</MetaItem>
                  <MetaItem>🏢 {req.department}</MetaItem>
                  <MetaItem>💰 {req.salaryBand}</MetaItem>
                </MetaInfo>
              </CardHeader>

              <CardBody>
                <StatsGrid>
                  <StatItem>
                    <StatValue>{req.daysOpen}</StatValue>
                    <StatLabel>Days Open</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{totalCandidates}</StatValue>
                    <StatLabel>Candidates</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{req.stageCount.interview}</StatValue>
                    <StatLabel>Interviews</StatLabel>
                  </StatItem>
                </StatsGrid>

                <Pipeline>
                  <PipelineLabel>Pipeline Distribution</PipelineLabel>
                  <PipelineBar>
                    {totalCandidates > 0 ? (
                      <>
                        {req.stageCount.applied > 0 && (
                          <PipelineSegment 
                            stage="applied" 
                            width={(req.stageCount.applied / totalCandidates) * 100}
                          >
                            {req.stageCount.applied}
                          </PipelineSegment>
                        )}
                        {req.stageCount.screening > 0 && (
                          <PipelineSegment 
                            stage="screening" 
                            width={(req.stageCount.screening / totalCandidates) * 100}
                          >
                            {req.stageCount.screening}
                          </PipelineSegment>
                        )}
                        {req.stageCount.interview > 0 && (
                          <PipelineSegment 
                            stage="interview" 
                            width={(req.stageCount.interview / totalCandidates) * 100}
                          >
                            {req.stageCount.interview}
                          </PipelineSegment>
                        )}
                        {req.stageCount.offer > 0 && (
                          <PipelineSegment 
                            stage="offer" 
                            width={(req.stageCount.offer / totalCandidates) * 100}
                          >
                            {req.stageCount.offer}
                          </PipelineSegment>
                        )}
                        {req.stageCount.hired > 0 && (
                          <PipelineSegment 
                            stage="hired" 
                            width={(req.stageCount.hired / totalCandidates) * 100}
                          >
                            {req.stageCount.hired}
                          </PipelineSegment>
                        )}
                      </>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '100%',
                        color: Colors.textTertiary,
                        fontSize: `${FontSizes.sm}px`
                      }}>
                        No candidates yet
                      </div>
                    )}
                  </PipelineBar>
                </Pipeline>
              </CardBody>

              <CardFooter>
                <ManagerInfo>
                  <Avatar>{getInitials(req.hiringManager)}</Avatar>
                  <ManagerName>{req.hiringManager}</ManagerName>
                </ManagerInfo>
                <SLAIndicator status={req.slaStatus}>
                  {req.slaStatus === 'ON_TRACK' && '✓'}
                  {req.slaStatus === 'AT_RISK' && '⚠'}
                  {req.slaStatus === 'BREACHED' && '✗'}
                  {req.slaStatus.replace('_', ' ')}
                </SLAIndicator>
              </CardFooter>
            </RequisitionCard>
          );
        })}
        </RequisitionsGrid>
        
        <CreateButton onClick={() => navigate('/requisitions/create')}>
          +
        </CreateButton>
      </ContentArea>
    </PageContainer>
  );
};

export default RequisitionsScreen;